import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { orderBy } from 'lodash-es';
import { Page, TableList, Typography, PageEmptyState, PageErrorStateIcon, Button, PageContentHeader, Callout, IconButton } from 'seagull';
import { Plus, Trash } from 'seagull/icons';
import InviteOrEditMemberModal from '~/components/InviteOrEditMemberModal';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { routes } from '~/constants';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { UserRole } from '~/models';
import { selectors } from '~/store/state-selectors';
import AccountantAccessApprovalModal from './components/AccountantAccessApprovalModal';
import RemoveAccountantModal from './components/RemoveAccountantModal';
import RemoveFromTeamModal from './components/RemoveFromTeamModal';
import MemberRow from './MemberRow';
import styles from './TeamPage.module.scss';
import { testPrefix } from './TeamPage.utils';
const columnDefs = [
    {
        width: [
            350,
            400
        ]
    },
    {
        width: 120
    },
    {
        width: 220
    }
];
const accountantsColumnDefs = columnDefs;
const roleOrder = {
    Owner: 1,
    Admin: 2,
    Member: 3
};
const roleToText = {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member'
};
export function TeamPageWithData(props) {
    const { members, accountants, loading, refresh } = props;
    const userRole = useStore(selectors.user.role);
    const userId = useStore(selectors.user.id);
    const businessId = useStore(selectors.user.businessId);
    const { api, logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const { ENABLE_KILL_ACCOUNTANT_ACCESS_FE } = useFeatureFlags();
    const { t: texts } = useLocaleKeys('pages.teamPage');
    const { t: teamTexts } = useLocaleKeys('pages.teamPage');
    const { t: commonTexts } = useLocaleKeys('common');
    const location = useLocation();
    const history = useHistory();
    const headerItems = [
        {
            id: 'memberName',
            value: texts.columnHeaders.memberName()
        },
        {
            id: 'role',
            value: texts.columnHeaders.role()
        },
        {
            id: 'status',
            value: texts.columnHeaders.status()
        }
    ];
    const accountantsHeaderItems = [
        {
            value: texts.accountantsColumnHeaders.companyName()
        },
        {
            value: texts.accountantsColumnHeaders.dateAdded()
        },
        {
            value: texts.accountantsColumnHeaders.status()
        }
    ];
    const membersList = useMemo(()=>{
        return orderBy(members.map((member)=>({
                user: member,
                role: roleToText[member.effectiveRole()],
                status: member.isInvited() ? 'Pending' : 'Active'
            })), [
            (m)=>roleOrder[m.role],
            'status'
        ], [
            'asc',
            'asc'
        ]);
    }, [
        members
    ]);
    const { invitedAccountants, activeAccountants } = useMemo(()=>{
        if (ENABLE_KILL_ACCOUNTANT_ACCESS_FE) {
            return {
                invitedAccountants: [],
                activeAccountants: []
            };
        }
        const normalized = accountants || [];
        return {
            invitedAccountants: normalized.filter((acc)=>acc.isInvited()),
            activeAccountants: normalized.filter((acc)=>acc.isActive())
        };
    }, [
        accountants,
        ENABLE_KILL_ACCOUNTANT_ACCESS_FE
    ]);
    useOnMount(()=>{
        var _location_state;
        if (((_location_state = location.state) === null || _location_state === void 0 ? void 0 : _location_state.openAccountantAccessRequest) && invitedAccountants.length > 0) {
            onOpenAccountantAccessRequest(invitedAccountants[0]);
            history.replace(routes.team);
        }
    });
    const onChangeRole = (member)=>{
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            biName: 'invite_or_edit_member_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(InviteOrEditMemberModal, {
                    member: member,
                    allMembers: members,
                    onClose: dismissModal,
                    onSuccess: refresh
                });
            }
        });
    };
    const onResendInvitation = async (member)=>{
        if (!businessId) return;
        const { success } = await handlePromise(api.member.resendInvitation(businessId, member.id()));
        if (!success) {
            return overlayContext.showErrorToast(commonTexts.toasts.failedToResendInvitation(), 'resend-invitation-failure');
        }
        overlayContext.showSuccessToast(texts.invitationResentTo({
            memberName: member.fullName()
        }), 'resend-invitation-success');
    };
    const onRemoveFromTeam = (member)=>{
        logBI(12202, {
            id: member.id(),
            email: member.email()
        });
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            biName: 'remove_from_team_modal',
            content: (p)=>/*#__PURE__*/ _jsx(RemoveFromTeamModal, _object_spread({
                    member: member,
                    allMembers: members,
                    onRefresh: refresh
                }, p))
        });
    };
    const onInviteMember = ()=>{
        overlayContext.showModal({
            disableEnforceFocus: true,
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            biName: 'invite_or_edit_member_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(InviteOrEditMemberModal, {
                    onClose: dismissModal,
                    onSuccess: refresh,
                    allMembers: members
                });
            }
        });
    };
    const onOpenAccountantAccessRequest = (accountant)=>{
        overlayContext.showModal({
            biName: 'accountant_access_approval_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(AccountantAccessApprovalModal, {
                    accountant: accountant,
                    dismissModal: dismissModal,
                    refresh: refresh
                });
            }
        });
    };
    const onRemoveAccountAccess = (accountant)=>{
        overlayContext.showModal({
            biName: 'remove_accountant_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (p)=>/*#__PURE__*/ _jsx(RemoveAccountantModal, _object_spread_props(_object_spread({}, p), {
                    accountant: accountant,
                    refresh: refresh
                }))
        });
    };
    return /*#__PURE__*/ _jsxs(Page, {
        header: texts.header({
            title: texts.title(),
            count: String(membersList.length)
        }),
        subheader: texts.subheader(),
        loading: loading,
        testId: testPrefix + '-screen',
        actions: /*#__PURE__*/ _jsx(Button, {
            intent: Intent.InviteTeamMember,
            prefixIcon: /*#__PURE__*/ _jsx(Plus, {}),
            onClick: onInviteMember,
            testId: testPrefix + '-btn-invite-member',
            bi: {
                name: 'invite_team_member'
            },
            children: commonTexts.buttons.inviteTeamMember()
        }),
        children: [
            invitedAccountants.length > 0 && /*#__PURE__*/ _jsx(PageContentHeader, {
                children: /*#__PURE__*/ _jsx("div", {
                    className: styles.callouts,
                    children: invitedAccountants.map((acc)=>/*#__PURE__*/ _jsx(Callout, {
                            withAction: /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'view_accountant_access_request'
                                },
                                skin: "primaryOutline",
                                intent: Intent.OpenAccountantAccessRequest,
                                testId: testPrefix + '-view-acc-request',
                                onClick: ()=>onOpenAccountantAccessRequest(acc),
                                children: commonTexts.buttons.viewDetails()
                            }),
                            children: teamTexts.pendingAccountantRequest()
                        }, acc.id()))
                })
            }),
            /*#__PURE__*/ _jsxs(TableList.Provider, {
                columnDefs: columnDefs,
                hasActionColumn: true,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        weight: "normal",
                        className: styles.tableTitle,
                        children: teamTexts.teamMembers()
                    }),
                    /*#__PURE__*/ _jsx(TableList.Header, {
                        items: headerItems,
                        testId: "".concat(testPrefix, "-table-header")
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        children: membersList.map((data, idx)=>/*#__PURE__*/ _jsx(MemberRow, {
                                data: data,
                                testId: "".concat(testPrefix, "-row-").concat(idx),
                                adminOrMemberActingOnOwner: data.user.effectiveRole() === UserRole.Owner && (userRole == UserRole.Admin || userRole == UserRole.Member),
                                isEditingTheSoleOwner: data.user.isOwner() && members.filter((m)=>m.isOwner()).length === 1,
                                isCurrentUser: data.user.id() === userId,
                                onChangeRole: onChangeRole,
                                onResendInvitation: onResendInvitation,
                                onRemoveFromTeam: onRemoveFromTeam
                            }, data.user.id()))
                    })
                ]
            }),
            activeAccountants.length > 0 && /*#__PURE__*/ _jsxs(TableList.Provider, {
                columnDefs: accountantsColumnDefs,
                hasActionColumn: true,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        weight: "normal",
                        className: cn(styles.tableTitle, styles.secondTableTitle),
                        children: teamTexts.accountantsWithAccess()
                    }),
                    /*#__PURE__*/ _jsx(TableList.Header, {
                        items: accountantsHeaderItems
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        "data-testid": testPrefix + '-acc-table-body',
                        children: activeAccountants.map((acc, idx)=>/*#__PURE__*/ _jsx(TableList.Row, {
                                items: [
                                    {
                                        value: acc.companyName()
                                    },
                                    {
                                        value: acc.formattedDate()
                                    },
                                    {
                                        value: /*#__PURE__*/ _jsx(Typography, {
                                            skin: "success",
                                            children: teamTexts.active()
                                        })
                                    }
                                ],
                                actionColumn: /*#__PURE__*/ _jsx(IconButton, {
                                    bi: {
                                        name: 'remove_accountant_access'
                                    },
                                    skin: "error",
                                    intent: Intent.RemoveAccountAccess,
                                    testId: "".concat(testPrefix, "-").concat(idx, "-rm-acc-access"),
                                    onClick: ()=>onRemoveAccountAccess(acc),
                                    children: /*#__PURE__*/ _jsx(Trash, {
                                        size: 20
                                    })
                                })
                            }, acc.id()))
                    })
                ]
            })
        ]
    });
}
export default function TeamPage() {
    const { api } = useStoreActions();
    const { t: texts } = useLocaleKeys('pages.teamPage');
    const { t: commonTexts } = useLocaleKeys('common');
    useSetDocumentTitle(texts.title());
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: ()=>api.member.getAll(),
        renderChild: (_0)=>{
            let { data } = _0, rest = _object_without_properties(_0, [
                "data"
            ]);
            return /*#__PURE__*/ _jsx(TeamPageWithData, _object_spread({}, data, rest));
        },
        renderError: (param)=>{
            let { refresh } = param;
            return /*#__PURE__*/ _jsx(PageEmptyState, {
                testId: "".concat(testPrefix, "-error-page"),
                illustration: /*#__PURE__*/ _jsx(PageErrorStateIcon, {}),
                message: texts.errorMessage(),
                actions: /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'refresh_team_page'
                    },
                    onClick: ()=>refresh(),
                    testId: "".concat(testPrefix, "-refresh-btn"),
                    children: commonTexts.buttons.refresh()
                })
            });
        }
    });
}
